<script>
import {defineComponent, reactive} from "vue";
import Icon from "@/javascript/views/components/Icon.vue";

export default defineComponent({
  components: {Icon},
  props: {
    /**
     * If true, will turn off overlay on content behind modal.
     */
    showBackground: {
      type: Boolean,
      default: false
    },
    /**
     * The title of the modal.
     */
    title: {
      type: String,
      default: ''
    },
    /**
     * Src attribute for banner image.
     */
    imageSrc: {
      type: String,
      default: ''
    },
    /**
     * Alt attribute for banner image.
     */
    imageAlt: {
      type: String,
      default: ''
    },
    /**
     * Modal size, scales to tailwind screen sizes
     */
    size: {
      type: String,
      default: 'medium',
      validator: (value) => {
        return ['popup', 'small', 'medium', 'large', 'screen'].includes(value);
      }
    },
    /**
     * Where the image should be positioned within the modal.
     */
    imagePosition: {
      type: String,
      default: 'top',
      validator: (value) => {
        return ['top', 'left'].includes(value);
      }
    },
    /**
     * The object-position for the image.
     * Any valid css position syntax.
     */
    imageObjectPosition: {
      type: String,
      default: 'center'
    },
    /**
     * Hide the close button for the modal.
     */
    hideCloseBtn: {
      type: Boolean,
      default: false
    },
    /**
     * Background color of the modal.
     * Can be any valid css color syntax.
     */
    backgroundColor: {
      type: String,
      default: '#FFFFFF'
    },
    /**
     * Text color of the modal.
     * Can be any valid css color syntax.
     */
    textColor: {
      type: String,
      default: '#000000'
    },
    /**
     * Can the modal be dismissed by clicking off of it
     */
    backgroundDismissible: {
      type: Boolean,
      default: true
    },
    /**
     * Makes the banner image smaller when position == top
     */
    shrinkBanner: {
      type: Boolean,
      default: false
    },
    noPadding: {
      type: Boolean,
      default: false
    }
  },

  emits: ['close'],
  setup(props) {

    props = reactive(props);
    return {
      classes: {
        'show-background': props.showBackground
      },
      contentClasses: {
        'base-modal-container': true,
        'clean-scrollbar': true,
        'base-container': true,
        'show-image': props.imageSrc,
        'modal-small-banner': props.shrinkBanner,
        'modal-no-padding': props.noPadding,
        [`image-position--${props.imagePosition || 'top'}`]: true,
        [`modal-size-${props.size || 'medium'}`]: true

      }
    };
  },
  data() {
    return {};
  },
  computed: {
    cssProps() {
      return {
        '--background-color': this.backgroundColor,
        '--text-color': this.textColor,
        '--image-object-position': this.imageObjectPosition
      };
    },
  },
  mounted() {
    // Need this to ensure the drawer is visible on mobile because VH doesn't behave on mobile apparently
    window.addEventListener('resize', this.setMaxSize);
    this.setMaxSize();
  },
  unmounted() {
    window.removeEventListener('resize', this.setMaxSize);
  },
  beforeMount() {
    this.setBodyOverflow(true);
  },
  beforeUnmount() {
    this.setBodyOverflow(false);
  },
  methods: {
    setBodyOverflow(show) {
      if (show) {
        document.body.style.overflow = 'hidden';
      } else {
        document.body.style.overflow = 'unset';
      }
    },
    close() {
      this.$emit('close');
    },
    backgroundClick() {
      if (this.backgroundDismissible) {
        this.$emit('close');
      }
    },
    // Sets the component to the actual viewport height & width to get around mobile browser & keyboard issues
    setMaxSize() {
      let screenHeight = document.documentElement.clientHeight;
      let screenWidth = document.documentElement.clientWidth;
      if (this.$refs.modal) {
        this.$refs.modal.style.maxHeight = `${screenHeight - 24}px`;
        this.$refs.modal.style.width = `${screenWidth}px`;
      }
      if (this.$refs.overlay) {
        this.$refs.overlay.style.height = `${screenHeight}px`;
      }
    }
  }
});

</script>

<template>
  <div
    id="base-modal-background"
    ref="overlay"
    :class="classes"
    :style="cssProps"
    @click.self="backgroundClick"
  >
    <div
      ref="modal"
      :class="contentClasses"
    >
      <button
        v-if="!hideCloseBtn"
        class="close-btn"
        @click="close"
      >
        <Icon

          icon="x"
          :color="textColor"
          size="24px"
        />
      </button>

      <img
        v-if="imageSrc"
        :src="imageSrc"
        :alt="imageAlt"
      >

      <header v-if="title">
        <h1
          :class="{'title-medium': true, 'mt-6': !imageSrc}"
        >
          {{ title }}
        </h1>
      </header>

      <slot name="header-slot" />
      <div class="modal-content min-w-full">
        <slot />
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
@import '@/styles/_variables.scss';

#base-modal-background {
  position: fixed;
  top: 0;
  left: 0;
  z-index: 10000;
  width: 100vw;
  display: flex;
  justify-content: center;
  align-items: center;
  padding: $space-4xl;

  &:not(.show-background) {
    background-color: $color-opacity-black-60;
  }

  .base-modal-container {
    background-color: var(--background-color);
    color: var(--text-color);
    padding: $space-xl;
    display: flex;
    flex-direction: column;
    position: relative;
    overflow-x: hidden;
    width: calc(100vw - 40px);


    &.modal-size-popup {
      @apply max-w-screen-xs;
    }

    &.modal-size-small {
      @apply max-w-screen-sm;
    }

    &.modal-size-medium {
      @apply max-w-screen-md;
    }

    &.modal-size-large {
      @apply max-w-screen-lg;
    }

    &.modal-size-screen {
      max-width: 100vw;
    }

    .close-btn {
      position: absolute;
      top: 20px;
      right: 20px;
      z-index: 1000;
    }

    &.show-image {
      padding-top: 224px;

      img {
        height: 200px;
        width: 100%;
      }

      @screen md {
        &.image-position--left {
          max-width: 1000px;
          padding-left: 274px;
          padding-top: 24px;

          img {
            height: 100%;
            width: 250px;
          }
        }
      }
    }

    img {
      object-fit: cover;
      object-position: var(--image-object-position);
      position: absolute;
      top: 0;
      left: 0;
    }

    header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding-bottom: $space-lg;
      margin-bottom: $space-lg;

      button {
        display: flex;
      }
    }

    &.modal-no-padding {
      padding: 0 !important;
    }
  }

  // tablet screens
  @media screen and (min-width: $breakpoint-min-sm) and (max-width: $breakpoint-max-lg) {
    padding: $space-3xl;
  }
  // mobile screens
  @media screen and (max-width: $breakpoint-max-sm) {
    padding: $space-md;

    .base-modal-container {
      padding: $space-md;
    }
  }
}
</style>
